import Konva from "konva";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import cloneDeep from "lodash/cloneDeep";
import { PagesProps } from "../dtos/PagesProps";
import api from "../services/api";

interface AllMyProjects {
  id: string;
  user_id: string;
  template: {
    arrayOfPages: PagesProps[];
  };
}
interface IHistory {
  currentStep: number;
  step: PagesProps[][];
}

interface UserAlreadyUsed {
  idUser: string;
  isActive: boolean;
}

interface MyProfile {
  user: {
    id: string;
    people_id: string;
    email: string;
    avatar: string;
    is_admin: boolean;
    is_blocked: boolean;
    user_type: string;
    created_at: string;
    updated_at: string;
    avatar_url: string;
  };
  people: {
    id: string;
    address_id: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    born_at: null | string;
    gender: null | string;
    cpf: null | string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  addresses: {
    id: string;
    zipcode: null | string;
    street: null | string;
    number: null | string;
    district: null | string;
    city: null | string;
    state: null | string;
    country: null | string;
    is_finished: false;
    created_at: string;
    updated_at: string;
  };
  contacts: {
    id: string;
    phone: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  is_finished_profile: boolean;
}

interface MainContextData {
  addToRefs: (element: Konva.Stage) => void;
  clearStages: () => void;
  currentTemplateInfo: any;
  objectScreen: PagesProps[];
  stageRef: React.MutableRefObject<any>;
  setObjectScreen: React.Dispatch<React.SetStateAction<PagesProps[]>>;
  objectSelectId: string;
  setObjectSelectId: (id: string) => void;
  dragUrl: React.MutableRefObject<any>;
  history: IHistory;
  setHistory: React.Dispatch<React.SetStateAction<IHistory>>;
  addToHistory: (isLastHistory?: boolean) => void;
  isUpLowerCase: boolean;
  setIsUpLowerCase: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGroup: Konva.Group;
  setSelectedGroup: React.Dispatch<React.SetStateAction<Konva.Group>>;
  documentName: React.MutableRefObject<string>;
  blockKeyboard?: boolean;
  setBlockKeyboard?: React.Dispatch<React.SetStateAction<boolean>>;
  clearHistory: () => void;
  loadThumbnail: boolean;
  setLoadThumbnail: React.Dispatch<React.SetStateAction<boolean>>;
  srcAvatar: string;
  setSrcAvatar: React.Dispatch<React.SetStateAction<string>>;
  refreshAvatar: boolean;
  setRefreshAvatar: React.Dispatch<React.SetStateAction<boolean>>;
  myProfileId: string;
  setUserUsingDocument: React.Dispatch<React.SetStateAction<UserAlreadyUsed>>;
  userUsingDocument: UserAlreadyUsed;
  activeUser: string;
  profile: MyProfile;
  setActiveUser: React.Dispatch<React.SetStateAction<string>>;
  documentID: React.MutableRefObject<string>;
  isModalCreateClause: boolean;
  setIsModalCreateClause: React.Dispatch<React.SetStateAction<boolean>>;
  templateIsLoaded: boolean;
  setTemplateIsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  isModalDiffAccept: boolean;
  handleOpenModalDiffAccept: (typeModal: "Accept" | "Reject") => void;
  handleCloseModalDiffAccept: () => void;
  nameOfCurrentUser?: React.MutableRefObject<string>;
  typeModal?: "Accept" | "Reject";
  setTypeModal?: React.Dispatch<React.SetStateAction<"Accept" | "Reject">>;
  triggerForEdit: "none" | "accept" | "reject";
  setTriggerForEdit: React.Dispatch<
    React.SetStateAction<"none" | "accept" | "reject">
  >;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalDiffAccept: React.Dispatch<React.SetStateAction<boolean>>;
  divRefModal: React.MutableRefObject<HTMLDivElement>;
  divRefModalDecline: React.MutableRefObject<HTMLDivElement>;
  handleOpenModalDifferences: (type: "Open" | "Close") => void;
  handleOpenModalDifferencesDecline: (type: "Open" | "Close") => void;
  whichOptionForModal: React.MutableRefObject<"Accept" | "Decline">;

  handleModificationsInDocument: () => void;
  hasModifications: boolean;
  setHasModifications: React.Dispatch<React.SetStateAction<boolean>>;
  isModalBlockFunctionality: boolean;
  setIsModalBlockFuncionality: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalBlockFuncionalities: (
    sourceModal: "download" | "automatizing" | "shared"
  ) => void;
  handleCloseModalBlockFuncionalities: () => void;
  sourceModalBlock: "download" | "automatizing" | "shared";
  setSourceModalBlock: React.Dispatch<
    React.SetStateAction<"download" | "automatizing">
  >;
  goToDownload: boolean;
  setGoToDownload: React.Dispatch<React.SetStateAction<boolean>>;
  strokeWidth?: React.MutableRefObject<string>;
  createdSharedDocument?: boolean;
  setCreatedSharedDocument?: React.Dispatch<React.SetStateAction<boolean>>;
}

const MainContext = createContext<MainContextData>({} as MainContextData);

const MainHookProvider: React.FC = ({ children }) => {
  const [objectScreen, setObjectScreen] = useState<PagesProps[]>([]);
  const [objectSelectId, setObjectSelectId] = useState("");
  const [loading, setLoading] = useState(false);
  const [blockKeyboard, setBlockKeyboard] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Konva.Group | null>(null);
  const [isUpLowerCase, setIsUpLowerCase] = useState<boolean>(false);
  const [loadThumbnail, setLoadThumbnail] = useState(false);
  const [isModalCreateClause, setIsModalCreateClause] = useState(false);
  const [hasModifications, setHasModifications] = useState(false);
  const [goToDownload, setGoToDownload] = useState(false);
  const [createdSharedDocument, setCreatedSharedDocument] = useState(false);
  const [isModalBlockFunctionality, setIsModalBlockFuncionality] =
    useState(false);
  const [isOwnerDocument, setIsOwnerDocument] = useState(false);
  const [profile, setProfile] = useState({} as MyProfile);
  const [templateIsLoaded, setTemplateIsLoaded] = useState(true);
  const [isModalDiffAccept, setIsModalDiffAccept] = useState(false);
  const [typeModal, setTypeModal] = useState<"Accept" | "Reject">(null);
  const [sourceModalBlock, setSourceModalBlock] = useState<
    "download" | "automatizing" | "shared"
  >(null);
  const whichOptionForModal = useRef<"Accept" | "Decline">("Accept");

  const nameOfCurrentUser = useRef("");
  const [triggerForEdit, setTriggerForEdit] = useState<
    "none" | "accept" | "reject"
  >("none");

  const handleOpenModalDiffAccept = useCallback(
    (type: "Accept" | "Reject") => {
      setTypeModal(type);
      setIsModalDiffAccept(true);
    },
    [isModalDiffAccept, typeModal]
  );

  const handleCloseModalDiffAccept = useCallback(() => {
    setIsModalDiffAccept(false);
  }, [isModalDiffAccept]);

  const [userUsingDocument, setUserUsingDocument] = useState({
    idUser: "",
    isActive: false
  } as UserAlreadyUsed);

  const strokeWidth = useRef(null);

  //just verify template
  // const verifyTemplate = objectScreen.forEach(object => {
  //   object.renderObjects.forEach(rendered => {
  //     if (rendered.isModify) {
  //     }
  //   });
  // });

  // modal for control when should be
  // block some funcionalities for any reason.
  const handleOpenModalBlockFuncionalities = useCallback(
    (sourceModal: "download" | "automatizing" | "shared") => {
      switch (sourceModal) {
        case "download":
          setIsModalBlockFuncionality(true);
          setSourceModalBlock("download");
          break;

        case "automatizing":
          setIsModalBlockFuncionality(true);
          setSourceModalBlock("automatizing");
          break;

        case "shared":
          setIsModalBlockFuncionality(true);
          setSourceModalBlock("shared");
          break;

        default:
          break;
      }
    },
    [isModalBlockFunctionality, sourceModalBlock]
  );

  const handleCloseModalBlockFuncionalities = useCallback(() => {
    setIsModalBlockFuncionality(false);
  }, [isModalBlockFunctionality]);

  //algorithm for detect if has pending modifications
  //in document that was already shared
  //for block things as download, unshared, etc...

  const handleModificationsInDocument = useCallback(() => {
    let changes = false;

    objectScreen.forEach((page, index) => {
      page.renderObjects.forEach((renderedObject, index) => {
        if (renderedObject.text && !changes) {
          if (renderedObject.isModify) {
            changes = true;
            setHasModifications(prevState => true);
          } else {
            changes = false;
          }
        }
      });
    });
  }, [objectScreen, hasModifications]);

  const divRefModal = useRef<HTMLDivElement>(null);

  const divRefModalDecline = useRef<HTMLDivElement>(null);

  const [srcAvatar, setSrcAvatar] = useState("");
  const [refreshAvatar, setRefreshAvatar] = useState(false);
  const [myProfileId, setMyProfileId] = useState("");
  const [activeUser, setActiveUser] = useState("");

  const documentID = useRef("");

  const handleOpenModalDifferences = useCallback(
    (type: "Open" | "Close") => {
      if (type === "Open") {
        divRefModal.current.style.visibility = "visible";
      }

      if (type === "Close") {
        divRefModal.current.style.visibility = "hidden";
      }
    },
    [divRefModal]
  );

  const handleOpenModalDifferencesDecline = useCallback(
    (type: "Open" | "Close") => {
      if (type === "Open") {
        divRefModalDecline.current.style.visibility = "visible";
      }

      if (type === "Close") {
        divRefModalDecline.current.style.visibility = "hidden";
      }
    },
    [divRefModalDecline]
  );

  useEffect(() => {
    const autoLoad = async () => {
      try {
        const responseProfile = await api.get("profile");
        setSrcAvatar(responseProfile?.data?.user?.avatar_url);
        setMyProfileId(responseProfile?.data?.user?.id);
        setProfile(responseProfile?.data);
      } catch (err) {
        console.log(err, "error when get profile");
      }
    };

    autoLoad();
  }, [refreshAvatar]);

  useEffect(() => {
    (async () => {
      const requestProfile = await api.get("profile");
      nameOfCurrentUser.current = `${requestProfile.data.people.first_name} ${requestProfile.data.people.last_name}`;
    })();
  }, [refreshAvatar]);

  const [history, setHistory] = useState<IHistory>({
    currentStep: 0,
    step: []
  });

  const currentTemplateInfo = useRef(null);
  const dragUrl = useRef(null);
  const stageRef = useRef<Konva.Stage[]>(null);

  const documentName = useRef("");
  stageRef.current = [];

  const addToRefs = useCallback(
    el => {
      if (el && !stageRef.current.includes(el)) {
        stageRef.current.push(el);
      }
    },
    [stageRef]
  );

  const clearStages = useCallback(() => {
    stageRef.current = [];
  }, []);

  const addToHistory = useCallback(
    (isLastHistory: boolean) => {
      let stages = cloneDeep(stageRef.current) as Konva.Stage[];
      let formatedObjectToApi: any = [];

      formatedObjectToApi = cloneDeep(objectScreen);

      setHistory(prevState => {
        let cloneState = cloneDeep(prevState);

        if (cloneState.currentStep === cloneState.step.length) {
          // If my current step is not behind from my last step

          if (!isLastHistory) {
            cloneState.currentStep = cloneState.step.length + 1;
          }

          cloneState.step.push(formatedObjectToApi);

          return cloneState;
        } else {
          // If my current step is behind from my last step
          //if you have steps ahead from your currentStep cut them off

          const slicedArray = cloneState.step.slice(
            0,
            cloneState.currentStep + 1
          );

          cloneState.currentStep = slicedArray.length;
          cloneState.step = cloneDeep(slicedArray);

          return cloneState;
        }
      });

      //setObjectScreen(formatedObjectToApi);
    },
    [stageRef, history, objectScreen]
  );

  const clearHistory = useCallback(() => {
    setHistory({ currentStep: 0, step: [] });
  }, []);

  return (
    <MainContext.Provider
      value={{
        addToRefs,
        addToHistory,
        clearStages,
        currentTemplateInfo,
        objectScreen,
        objectSelectId,
        stageRef,
        dragUrl,
        history,
        setObjectScreen,
        setObjectSelectId,
        setHistory,
        isUpLowerCase,
        setIsUpLowerCase,
        selectedGroup,
        setSelectedGroup,
        documentName,
        blockKeyboard,
        setBlockKeyboard,
        clearHistory,
        loadThumbnail,
        setLoadThumbnail,
        srcAvatar,
        setSrcAvatar,
        refreshAvatar,
        setRefreshAvatar,
        myProfileId,
        userUsingDocument,
        setUserUsingDocument,
        activeUser,
        profile,
        setActiveUser,
        documentID,
        isModalCreateClause,
        setIsModalCreateClause,
        templateIsLoaded,
        setTemplateIsLoaded,
        handleCloseModalDiffAccept,
        handleOpenModalDiffAccept,
        isModalDiffAccept,
        nameOfCurrentUser,
        typeModal,
        setTypeModal,
        triggerForEdit,
        setTriggerForEdit,
        loading,
        setLoading,
        setIsModalDiffAccept,
        divRefModal,
        handleOpenModalDifferences,
        whichOptionForModal,
        divRefModalDecline,
        handleOpenModalDifferencesDecline,
        hasModifications,
        setHasModifications,
        handleModificationsInDocument,
        isModalBlockFunctionality,
        setIsModalBlockFuncionality,
        handleCloseModalBlockFuncionalities,
        handleOpenModalBlockFuncionalities,
        sourceModalBlock,
        setSourceModalBlock,
        goToDownload,
        setGoToDownload,
        strokeWidth,
        createdSharedDocument,
        setCreatedSharedDocument
      }}
    >
      {children}
    </MainContext.Provider>
  );
};

// creating hook

function useMainHook(): MainContextData {
  const context = useContext(MainContext);

  if (!context) {
    throw new Error("useAuth must be used with an MainHookProvider");
  }

  return context;
}

export { MainHookProvider, useMainHook };
